import { notification } from "antd";
import dayjs, { Dayjs, isDayjs, tz } from "dayjs";
import weekday from "dayjs/plugin/weekday";
import Helper from "../../Helper";
import PageContentType from "../../PageContentType";
import { Material } from "../../models/Material";
import { Order } from "../../models/Order";
import { leadTimeUnitType, statusValues } from "../../models/Order.model";
import { ProcurementFilter } from "../../models/ProcurementFilter";
import { Submittal } from "../../models/Submittal";
import PlotAPI from "../../plotAPI";
import TeamInfo from "../../teamInfo";
import { ViewInfo } from "./materialTypes";

dayjs.extend(weekday);

// TODO: This IS locale aware. We may need to address this in the future if we have internationalization needs.
//       From my understanding, in construction in the united states the week starts on Sunday, and ends on saturday.
//       Our calculations for what a "week" is are based on that assumption.
export function startOfWeek(date: Dayjs): Dayjs {
    return date.weekday(0).startOf("day");
}

// TODO: This IS locale aware. We may need to address this in the future if we have internationalization needs.
//       From my understanding, in construction in the united states the week starts on Sunday, and ends on saturday.
//       Our calculations for what a "week" is are based on that assumption.
export function endOfWeek(date: Dayjs): Dayjs {
    return date.weekday(6).endOf("day");
}

export async function initialFilterInfo(
    projectInfo: any,
    pageContentType: PageContentType | null
): Promise<ProcurementFilter> {
    // The logic for determining the initial filter info is as follows:
    // 1. If there are filter params in the url, use those to set the initial filter info. This allows for sharing of links with specific filters applied.
    // 2. Else, if there is a cookie with a filter id, attempt to load that filter and use it as the initial filter info.
    // 3. Else, use a blank filter info object as the default.
    const urlParams = new URLSearchParams(window.location.search);
    if (
        urlParams.get("textSearch") ||
        urlParams.getAll("orderStatus").length > 0 ||
        urlParams.getAll("filterTeams").length > 0 ||
        urlParams.getAll("filterVendors").length > 0
    ) {
        const filterInfo = ProcurementFilter.blank(0, tz.guess());
        filterInfo.textSearch = urlParams.get("textSearch");
        filterInfo.orderStatus = urlParams.getAll("orderStatus");

        filterInfo.receivingTeams = [...urlParams.getAll("filterTeams")].map((team) => {
            return { id: parseInt(team) };
        });
        filterInfo.vendors = [...urlParams.getAll("filterVendors")].map((vendor) => {
            return { id: parseInt(vendor) };
        });

        return filterInfo;
    }
    const filterInfoCookieId = parseInt(Helper.getCookie("procurement_filter_id"));

    if (!Number.isFinite(filterInfoCookieId)) {
        Helper.deleteCookie("procurement_filter_id");
    } else if (filterInfoCookieId && projectInfo && pageContentType === PageContentType.PROJECT) {
        const filter = await PlotAPI.getProcurementFilterById(projectInfo, filterInfoCookieId);
        if (filter) {
            return filter;
        } else {
            Helper.deleteCookie("procurement_filter_id");
        }
    }

    return ProcurementFilter.blank(projectInfo?.id || 0, projectInfo?.timezone || "UTC");
}

export function initialViewInfo(pageContentType: PageContentType | null): ViewInfo {
    let viewInfo = new ViewInfo();
    const viewInfoCookie = Helper.getCookie("procurement_view_info");

    if (viewInfoCookie) {
        viewInfo = JSON.parse(viewInfoCookie);

        if (!viewInfo.sortDirection) {
            viewInfo.sortDirection = "asc";
        }

        // If their cookie was last set for group by project, then they were previously using the team page, but the project page does not do group by project (there would always be 1 project),
        // so we default it to group by spec.
        if (pageContentType == PageContentType.PROJECT && viewInfo.groupByValue == "PROJECT") {
            viewInfo.groupByValue = "SPEC";
        }
    }
    const urlParams = new URLSearchParams(window.location.search);
    // If there are any filter params in the url, override the default filterInfo with the values from the url
    // Else, just chain the sent filterInfo through to be returned.

    const groupBy = urlParams.get("groupBy");
    const currentTab = urlParams.get("currentTab");

    if (currentTab && ["DASHBOARD", "SUMMARY", "ACTION_ITEMS", "MATERIALS", "ORDERS"].includes(currentTab)) {
        // @ts-expect-error: The if statement above ensures that this is a valid value
        viewInfo.currentTab = currentTab;
    } else if (window.location.pathname.includes("checklist")) viewInfo.currentTab = "ACTION_ITEMS";

    if (groupBy && ["NONE", "TEAM", "VENDOR", "SPEC"].includes(groupBy)) {
        // @ts-expect-error: The if statement above ensures that this is a valid value
        viewInfo.groupByValue = urlParams.get("groupBy");
    }
    if (viewInfo.orderViewValue == "-1" || !viewInfo.orderViewValue) {
        viewInfo.orderViewValue = "ALL";
    }
    if (!viewInfo.checklistViewValue) {
        viewInfo.checklistViewValue = "chart";
    }

    return viewInfo;
}

export const materialSpecNumberHelper = (item: Order | Material): string => {
    if (item instanceof Order) {
        return specString(item.material?.specNumber || null, item.material?.submittalNumber || null);
    }

    return specString(item.specNumber || null, item.submittalNumber || null);
};

export function partialSpecToFullSpec(spec: string | null | undefined): string {
    const baseSpec = spec || "";
    const cleanBaseSpec = baseSpec.replace(/-|\s/g, "");

    return cleanBaseSpec.slice(0, 6).padEnd(6, "0");
}

export function partialSubToFullSub(sub: string | null | undefined): string {
    const baseSub = sub || "";
    const cleanBaseSub = baseSub.replace(/-|\s/g, "");

    return cleanBaseSub.slice(0, 3).padEnd(3, "0");
}

export function specString(spec_number: string | null | undefined, sub_number: string | null | undefined): string {
    spec_number = partialSpecToFullSpec(spec_number);
    sub_number = partialSubToFullSub(sub_number);

    return spec_number + "-" + sub_number;
}

export function validMaterialSpec(spec: string): boolean {
    // Check if the string only contains numbers or dashes
    const regex = /^[0-9]+(-[0-9]+)?$/;

    return regex.test(spec);
}

export function doesNeedSubmitted(material: Material, submittals: Submittal[]): boolean {
    if (material instanceof Material && material.materialsSubmittals?.length === 0) {
        return false;
    }

    if (material.submitDate) {
        return false;
    }

    if (submittals.every((submittal) => !submittal.submitDate)) {
        return false;
    }

    return true;
}

export function doesNeedApproved(material: Material, submittals: Submittal[]): boolean {
    if (material instanceof Material && material.materialsSubmittals?.length === 0) {
        return false;
    }

    if (material.approvalDate) {
        return false;
    }

    if (submittals.every((submittal) => !submittal.approvalDate)) {
        return false;
    }

    return true;
}

export function doesNeedRevisionAddressed(material: Material, submittals: Submittal[]): boolean {
    if (material.materialsSubmittals?.length === 0) {
        return false;
    }

    if (submittals.find((submittal) => submittal.needsReviseAndResubmit)) {
        return true;
    }

    if (submittals.find((submittal) => !submittal.currentRevision)) {
        return true;
    }

    return false;
}

export function standardWidthDate(date: Date | Dayjs | null): string {
    if (isDayjs(date)) {
        date = date.toDate();
    }

    if (date) {
        return date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
        });
    }

    return "--/--/--";
}

export function standardWidthDateTime(date: Date | null): string {
    if (date) {
        return date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    }

    return "--/--/--";
}

export function reducedStandardWidthDateTime(date: Date | null): string {
    if (date) {
        const dateStr = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
        });

        const timeStr = date
            .toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            })
            .replace(" ", "")
            .toLocaleLowerCase();

        return dateStr + " " + timeStr;
    }

    return "--/--/--";
}

function actionsForMaterial(material: Material): MaterialActionItem[] {
    const actions: MaterialActionItem[] = [];

    const submitBy = material.submitBy;
    if (submitBy && !material.submitDate) {
        actions.push({ material, action: "submitBy", date: submitBy.toDate() });
    }

    const approveBy = material.approveBy;
    if (approveBy && !material.approvalDate) {
        actions.push({ material, action: "approveBy", date: approveBy.toDate() });
    }

    return actions;
}

function actionsForOrder(order: Order, rojFlag: boolean = true): OrderActionItem[] {
    const actions: OrderActionItem[] = [];

    if (order.orderBy && !order.ordered && statusValues[order.status] <= statusValues["needs_ordered"]) {
        actions.push({ order, action: "orderBy", date: order.orderBy.toDate() });
    }

    if (order.needsCheckIn) {
        if (order.ordered) {
            actions.push({ order, action: "checkInAos", date: new Date() });
        } else {
            actions.push({ order, action: "checkInLeadTime", date: new Date() });
        }
    }

    if (
        order.requiredOnJobsite &&
        order.anticipatedOnJobsite &&
        !order.delivery &&
        statusValues[order.status] <= statusValues["needs_scheduled"]
    ) {
        actions.push({ order, action: "deliveryBy", date: order.requiredOnJobsite.toDate() });
    }

    if (!order.leadTime && statusValues[order.status] <= statusValues["needs_lead_time"]) {
        actions.push({ order, action: "leadTime", date: new Date() });
    }

    if (!order.requiredOnJobsite && statusValues[order.status] <= statusValues["needs_roj"] && rojFlag) {
        actions.push({ order, action: "requiredOnSite", date: new Date() });
    }

    return actions;
}

function actionsForMaterialForWeek(material: Material, endDate: Dayjs): MaterialActionItem[] {
    const actions: MaterialActionItem[] = [];

    const submitBy = material.submitBy;
    if (submitBy && !material.submitDate && submitBy.isBefore(endDate)) {
        actions.push({ material, action: "submitBy", date: submitBy.toDate() });
    }

    const approveBy = material.approveBy;
    if (approveBy && !material.approvalDate && approveBy.isBefore(endDate)) {
        actions.push({ material, action: "approveBy", date: approveBy.toDate() });
    }

    return actions;
}

function actionsForOrderForWeek(order: Order, endDate: Dayjs, rojFlag: boolean = true): OrderActionItem[] {
    // returnRoj is a flag to determine if the required on jobsite date should be returned as an action
    // A setting on the project is toggled that only allowes GC to edit ROJ dates.
    // If that setting is true and the current user team is not the GC team, then the required on jobsite date should not be returned
    const actions: OrderActionItem[] = [];

    if (
        order.orderBy &&
        !order.ordered &&
        order.orderBy.isBefore(endDate) &&
        statusValues[order.status] <= statusValues["needs_ordered"]
    ) {
        actions.push({ order, action: "orderBy", date: order.orderBy.toDate() });
    }

    if (order.needsCheckIn) {
        if (order.ordered) {
            actions.push({ order, action: "checkInAos", date: new Date() });
        } else {
            actions.push({ order, action: "checkInLeadTime", date: new Date() });
        }
    }

    if (
        order.anticipatedOnJobsite &&
        order.requiredOnJobsite &&
        order.requiredOnJobsite.isBefore(endDate) &&
        !order.delivery &&
        statusValues[order.status] <= statusValues["needs_scheduled"]
    ) {
        actions.push({ order, action: "deliveryBy", date: order.requiredOnJobsite.toDate() });
    }

    if (!order.leadTime && statusValues[order.status] <= statusValues["needs_lead_time"]) {
        actions.push({ order, action: "leadTime", date: new Date() });
    }

    if (!order.requiredOnJobsite && statusValues[order.status] <= statusValues["needs_roj"] && rojFlag) {
        actions.push({ order, action: "requiredOnSite", date: new Date() });
    }

    return actions;
}

// These classes could be implemented with a template type, but that would definitely make this code less readable
export class MaterialActionItem {
    material: Material;
    action: string;
    date: Date;
}

export class OrderActionItem {
    order: Order;
    action: string;
    date: Date;
}

export class CheckListItems {
    requiredOnSite: Order[] = [];
    leadTime: Order[] = [];
    submitBy: Material[] = [];
    approveBy: Material[] = [];
    orderBy: Order[] = [];
    deliveryBy: Order[] = [];
    checkInLeadTime: Order[] = [];
    checkInAos: Order[] = [];

    constructor() {
        this.requiredOnSite = [];
        this.leadTime = [];
        this.submitBy = [];
        this.approveBy = [];
        this.orderBy = [];
        this.deliveryBy = [];
        this.checkInLeadTime = [];
        this.checkInAos = [];
    }

    copy(): CheckListItems {
        const newCheckListItems = new CheckListItems();

        newCheckListItems.submitBy = this.submitBy.map((item) => Object.create(item as Material));
        newCheckListItems.approveBy = this.approveBy.map((item) => Object.create(item as Material));

        newCheckListItems.leadTime = this.leadTime.map((item) => Object.create(item as Order));
        newCheckListItems.orderBy = this.orderBy.map((item) => Object.create(item as Order));
        newCheckListItems.deliveryBy = this.deliveryBy.map((item) => Object.create(item as Order));
        newCheckListItems.checkInLeadTime = this.checkInLeadTime.map((item) => Object.create(item as Order));
        newCheckListItems.checkInAos = this.checkInAos.map((item) => Object.create(item as Order));
        newCheckListItems.requiredOnSite = this.requiredOnSite.map((item) => Object.create(item) as Order);

        return newCheckListItems;
    }
}

export function itemsNeedingAction(materials: Material[]): {
    materials: MaterialActionItem[];
    orders: OrderActionItem[];
} {
    let materialsNeedingAction: MaterialActionItem[] = [];
    let ordersNeedingAction: OrderActionItem[] = [];

    materials?.map((material) => {
        if (material.archived) {
            return;
        }

        materialsNeedingAction = materialsNeedingAction.concat(actionsForMaterial(material));

        material.orders?.map((order) => {
            if (order.archived) {
                return;
            }

            order.material = material;

            ordersNeedingAction = ordersNeedingAction.concat(actionsForOrder(order));
        });
    });

    return {
        materials: materialsNeedingAction,
        orders: ordersNeedingAction,
    };
}

export const itemsNeedingActionForWeek = (
    materials: Material[],
    endDate: Dayjs,
    project: any,
    team: TeamInfo
): CheckListItems => {
    const checkListItems = new CheckListItems();

    if (materials.length == 0) {
        return checkListItems;
    }

    materials?.map((material) => {
        if (material.archived) {
            return;
        }

        const materialActions = actionsForMaterialForWeek(material, endDate);

        materialActions.map((item) => {
            // @ts-expect-error: Dynamic key access
            checkListItems[item.action].push(item.material);
        });

        material.orders?.map((order) => {
            if (order.archived) {
                return;
            }

            order.material = material;

            const orderActions = actionsForOrderForWeek(order, endDate, returnRojLogic(project, team));

            orderActions.map((item) => {
                // @ts-expect-error: Dynamic key access
                checkListItems[item.action].push(item.order);
            });
        });
    });

    return checkListItems;
};

export const durationValidation = (value: string) => {
    if (value == undefined || value == null || value == "") {
        // Allow clearing the value out in this special case
        return false;
    }

    const durationIntegers = parseInt(value.replace(/\D/g, ""));

    // Ensure a number is pressent in the value
    if (!durationIntegers || durationIntegers == -1) {
        notification.error({
            key: "durationValidation",
            message: "Duration must be a number",
            duration: 2,
        });
        return;
    }

    // If lead_time has a value check if it's less than 0 or greater than 9999
    if (durationIntegers <= 0 || durationIntegers > 9999) {
        notification.error({
            key: "durationValidation",
            message: "Duration must be between 0 and 9999",
            duration: 2,
        });
        return false;
    }

    const duration_unit_type = value?.toString().match(/[a-zA-Z]+/g);

    const accepted_units = ["d", "w", "m", "y"];

    // Ensure a unit type is provded and
    if (duration_unit_type && !accepted_units.includes(duration_unit_type[0])) {
        notification.error({
            message: "Duration unit type must be one of the following: d, w, m, y",
            duration: 2,
        });
        return false;
    }

    return true;
};

export type DurationValuesUnit = "days" | "weeks" | "months" | "years" | null;

export interface DurationValues {
    value: number | null;
    unit: DurationValuesUnit;
}

export function durationInputChange(duration: string) {
    const durationValues = {} as DurationValues;
    if (!duration) {
        return durationValues;
    }
    const value = duration.match(/\d+/g);
    if (value) {
        durationValues.value = parseInt(value[0]);
    }
    const unit = duration.match(/[a-zA-Z]+/g);
    if (unit) {
        if (unit[0] == "d") {
            durationValues.unit = "days";
        }
        if (unit[0] == "w") {
            durationValues.unit = "weeks";
        }
        if (unit[0] == "m") {
            durationValues.unit = "months";
        }
        if (unit[0] == "y") {
            durationValues.unit = "years";
        }
    } else {
        durationValues.unit = null;
    }
    return durationValues;
}

export const renderDuration = (
    duration: number | null | undefined,
    durationUnit: "days" | "weeks" | "months" | "years" | null | undefined
) => {
    if (duration == null || durationUnit == null) {
        return "None";
    }

    return `${duration}${durationUnit?.charAt(0)}`;
};

export const durationInDays = (
    duration: number | null | undefined,
    durationUnit: "days" | "weeks" | "months" | "years" | null | undefined,
    cycleModifier: number = 1
): number => {
    if (duration == null || durationUnit == null) {
        return 0;
    }

    switch (durationUnit) {
        case "days":
            return duration * cycleModifier;
        case "weeks":
            return duration * cycleModifier * 7;
        case "months":
            return duration * cycleModifier * 30;
        case "years":
            return duration * cycleModifier * 365;
        default:
            return 0;
    }
};

export const calculateDifferenceInDays = (date1: Dayjs, date2: Dayjs) => {
    const startDate = date1.startOf("day");
    const endDate = date2.startOf("day");
    return startDate.diff(endDate, "days");
};

export const formatOrderMaterialName = (order: Order) => {
    let val = "";

    if (order.orderName) {
        val += `${order.orderName} - `;
    }

    val += order.material?.materialName;

    return val;
};
export const findDivisionNumberForOrder = (order: Order) => {
    if (order.material?.specNumber) {
        return order.material.specNumber.substring(0, 2);
    } else {
        return null;
    }
};

export const returnRojLogic = (project: any, team: TeamInfo) => {
    // TODO: Send this check to the backend
    // Check if the project has enabled only the owning team to set ROJ
    // If that team has it enabled and they're the owning team then allow it through

    const isOwningTeam = project.owning_team_id == team.id;

    // Enabled so check if they're the owning team or not
    if (project.lead_times_required_on_jobsite_require_gc) {
        return isOwningTeam;
    }

    // Not enabled so return true
    return true;
};

// TODO: This seems to work fine, but should we also deep copy it's orders?
export const deepCopyMaterial = (material: Material): Material => {
    return Object.create(material) as Material;
};

export const materialName = (material: Material): string => {
    return material.materialName || "";
};

export const orderName = (order: Order): string => {
    const name = order.orderName || "Order";

    if (order.material) {
        return name + " - " + materialName(order.material);
    }

    return name;
};

export const calculateDuration = (count: number, unitType: "days" | "weeks" | "months" | "years"): number => {
    if (count < 0 || isNaN(count)) {
        return 0; // Ensure count is not negative
    }
    switch (unitType) {
        case "days":
            return count;
        case "weeks":
            return count * 7;
        case "months":
            return count * 30;
        case "years":
            return count * 365;
    }
};

export const calculateReviewDuration = (
    count: number,
    unitType: "days" | "weeks" | "months" | "years",
    cycleModifier: number
): number => {
    if (count < 0 || isNaN(count)) {
        return 0; // Ensure count is not negative
    }
    switch (unitType) {
        case "days":
            return count * cycleModifier;
        case "weeks":
            return count * 7 * cycleModifier;
        case "months":
            return count * 30 * cycleModifier;
        case "years":
            return count * 365 * cycleModifier;
    }
};

export const newLeadTimeFromAnticipated = (
    orderDate: Dayjs,
    newAnticipatedDate: Dayjs,
    currentLeadTimeUnit: leadTimeUnitType
): { leadTime: number | null; unit: "days" | "weeks" } => {
    const diff = newAnticipatedDate.diff(orderDate, "days");

    if (diff > 0) {
        // If the current lead time unit is weeks and the difference is a multiple of 7, return in weeks.
        // We mostly do this to keep the unit that the user selected if we can keep it.
        if (currentLeadTimeUnit === "weeks" && diff % 7 === 0) {
            return { leadTime: diff / 7, unit: "weeks" };
        }

        return { leadTime: diff, unit: "days" };
    }

    return { leadTime: null, unit: "days" };
};

export const ProcurementColors = {
    submit: {
        bgColorCard: "bg-plot-submit/10",
        bgColor: "bg-plot-submit",
        borderColor: "border-plot-submit",
        iconColor: "text-plot-submit",
        textColor: "text-plot-submit",
        hexColor: "#22c55e",
    },
    approve: {
        bgColorCard: "bg-plot-approve/10",
        bgColor: "bg-plot-approve",
        borderColor: "border-plot-approve",
        iconColor: "text-plot-approve",
        textColor: "text-plot-approve",
        hexColor: "#a855f7",
    },
    leadtime: {
        bgColorCard: "bg-plot-leadtime/10",
        bgColor: "bg-plot-leadtime",
        borderColor: "border-plot-leadtime",
        iconColor: "text-plot-leadtime",
        textColor: "text-plot-leadtime",
        hexColor: "#3b82f6",
    },
    order: {
        bgColorCard: "bg-plot-order/10",
        bgColor: "bg-plot-order",
        borderColor: "border-plot-order",
        iconColor: "text-plot-order",
        textColor: "text-plot-order",
        hexColor: "#3b82f6",
    },
    checkin: {
        bgColor: "bg-plot-checkin",
        bgColorCard: "bg-plot-checkin/10",
        borderColor: "border-plot-checkin",
        iconColor: "text-plot-checkin",
        textColor: "text-plot-checkin",
        hexColor: "#8ee3ef",
    },
    requireOnSite: {
        bgColor: "bg-plot-ros",
        bgColorCard: "bg-plot-ros/10",
        borderColor: "border-plot-ros",
        iconColor: "text-plot-ros",
        textColor: "text-plot-ros",
        hexColor: "#fe5f55",
    },
    receive: {
        bgColorCard: "bg-plot-receive/10",
        bgColor: "bg-plot-receive",
        borderColor: "border-plot-receive",
        iconColor: "text-plot-receive",
        textColor: "text-orange-700",
        hexColor: "#ff9e00",
    },
};
export const ProcurementStrings = {
    submit: {
        ontimeLabel: "On-Time Submissions",
        dueLabel: "Submissions Due",
        byDueLabel: "Submit By",
        openLabel: "Open Submissions",
        shortLabel: "Submit",
        plural: "Submissions",
        pastTense: "Submitted",
    },
    approve: {
        ontimeLabel: "On-Time Approvals",
        dueLabel: "Approvals Due",
        byDueLabel: "Approve By",
        openLabel: "Open Approvals",
        shortLabel: "Approve",
        plural: "Approvals",
        pastTense: "Approved",
    },
    order: {
        ontimeLabel: "On-Time Orders",
        dueLabel: "Orders Due",
        byDueLabel: "Order By",
        openLabel: "Open Orders",
        shortLabel: "Order",
        plural: "Orders",
        pastTense: "Ordered",
    },
    checkin: {
        ontimeLabel: "On-Time Check-Ins",
        dueLabel: "Check-Ins Due",
        byDueLabel: "Check-In By",
        openLabel: "Open Check-Ins",
        shortLabel: "Check-In",
        plural: "Check-Ins",
        pastTense: "Checked-In",
    },
    receive: {
        ontimeLabel: "On-Time Deliveries",
        dueLabel: "Deliveries Due",
        byDueLabel: "Receive By",
        openLabel: "Open Deliveries",
        shortLabel: "Deliver",
        plural: "Deliveries",
        pastTense: "Delivered",
    },
    requireOnSite: {
        ontimeLabel: "On-Time Required On-Site",
        dueLabel: "Required On-Site Due",
        byDueLabel: "Required On-Site By",
        openLabel: "Open Required On-Site",
        shortLabel: "ROJ",
        plural: "Required On-Site",
        pastTense: "Required On-Site",
    },
    leadtime: {
        ontimeLabel: "On-Time Lead Times",
        dueLabel: "Lead Times Due",
        byDueLabel: "Lead Time By",
        openLabel: "Open Lead Times",
        shortLabel: "LT",
        plural: "Lead Times",
        pastTense: "Lead Time",
    },
};

export type ProcurementColumnType = {
    column: string;
    visible: boolean;
};
